/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';

// TODO Unit testy.
/**
 * Poskytovatel zdrojů pro zobrazování PDF dokumentů.
 * @implements {ViewerSourceCreator}
 */
class PdfViewerSourceCreator {
  /**
   * Vytvoří instanci.
   * @param {number} max Maximální počet současně zobrazovaných PDF.
   * @param {PoolAsyncFactory} poolFactory
   * @param {PdfRecycler} recycler
   * @param {PdfViewerSourceFactory} sourceFactory
   */
  constructor(max, poolFactory, recycler, sourceFactory) {
    this.pool = poolFactory.create(sourceFactory, { max });
    this.poolFactory = poolFactory;
    this.poolMap = {};
    this.recycler = recycler;
    this.sourceFactory = sourceFactory;
  }

  /** @inheritdoc */
  delete(source) {
    return new Promise((resolve) => {
      source.getFrame().repair();
      const pool = this.findPool(source);
      return resolve(pool.delete(source));
    });
  }

  /** @inheritdoc */
  destroy() {
    // Prázdná metoda pro splnění rozhraní.
    // TODO #295 Zrevidovat rušení u ViewerSourceCreator?
  }

  /**
   * Nalezne pool, ke kterému patří vybraný zdroj.
   * @param {PdfViewerSource} source
   * @private
   * @returns {PoolAsync}
   */
  findPool(source) {
    const foundPool = Object.values(this.poolMap).find((pool) =>
      pool.isLent(source)
    );
    if (isNil(foundPool)) {
      throw new Error(`Object ${source} is not lent.`);
    }
    return foundPool;
  }

  /** @inheritdoc */
  getLent() {
    return Object.values(this.poolMap).reduce(
      (acc, pool) => acc - pool.getAvailable(),
      this.pool.getLent()
    );
  }

  /** @inheritdoc */
  getMax() {
    return this.pool.getMax();
  }

  /** @inheritdoc */
  lend(parentEl, scheduleInfo) {
    return new Promise((resolve) => {
      const { poolMap, sourceFactory } = this;
      sourceFactory.setParentEl(parentEl);
      sourceFactory.setScheduleInfo(scheduleInfo);
      return resolve(
        scheduleInfo.getUrl().then((url) => {
          const existingPool = poolMap[url];
          if (!isNil(existingPool)) {
            return existingPool.lend();
          }
          // TODO #295 Nejprve vypůjčit a až pak tvořit nový pool?
          const createdPool = this.poolFactory.create({
            create: () => this.pool.lend(),
            delete: (source) => this.pool.delete(source)
          });
          poolMap[url] = createdPool;
          return createdPool.lend();
        })
      );
    });
  }

  /** @inheritdoc */
  receive(source) {
    return new Promise((resolve) =>
      resolve(
        this.recycler.recycle(source.getParent()).then(() => {
          const pool = this.findPool(source);
          pool.receive(source);
        })
      )
    );
  }

  /** @inheritdoc */
  replace() {
    return new Promise((resolve) =>
      resolve(
        Object.entries(this.poolMap).reduce(
          (acc, [url, pool]) =>
            acc.then(() => {
              if (pool.getLent() > 0) {
                return;
              }
              return pool.destroy().then(() => {
                delete this.poolMap[url];
              });
            }),
          Promise.resolve()
        )
      )
    );
  }
}

export default PdfViewerSourceCreator;
